import { Flex } from 'features/comments/modals/comment-center.style';
import { TextInfo } from 'features/forms/components/sections/Style';
import { CheckBox } from 'features/journey-map/modals/AdvancedSearch/Style';
import { memo, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { allInsightTypes, getAllTouchpoints, getSessionId, getStagesDetails, getSwimlane } from 'redux/memoizedSelectors';
import Loader from 'shared/components/spinners/Loaster';
import { Label_ts } from 'shared/cx-components/cx-components.style';
import { IconDispatcher } from 'shared/Icons';
import { Button } from 'shared/ui-basics';
import InsightType from 'shared/ui-basics/ui-components/InsightType';
import { getLanguagebyCode, pictureValidation, StripHtml } from 'utils/helpers';
import { alexAssistantCjm } from '../reducer/ai-actions';
import styled from 'styled-components';
import Editor from 'shared/components/Editor/Editor';
import { updateCjmDescription } from '../../journey-map/reducer/CJMActions';
import { useLocation } from 'react-router-dom';
import { getAllMapsParams } from 'features/comments/helpers';

type Props = {
    onClose: (all?: boolean) => void;
    getPrompt: any;
    onSave: (items: any) => void;
    stageId: string
    swimlaneKey: string
    max_tokens?: number
}
const checkDescription = (description: string) => {
    if (description.trim() === "" || description === `<p class="my-custom-tiptap-class"></p>`) {
        return true
    }
    return false
}
function AISuggestion({ onClose, getPrompt, onSave, stageId, swimlaneKey, max_tokens = 1000 }: Props) {

    const { t, i18n } = useTranslation();
    const { mapName, mapDescription, stageName, allRelatedInsights, allRelatedTouchpoints,
        relatedOpportunities, relatedSolutions, insightTypes } = useSelector(getStagesDetails(stageId));
    const sessionId = useSelector(getSessionId);
    const allTouchpoints = useSelector(getAllTouchpoints)
    const allTypes = useSelector(allInsightTypes)
    const language = getLanguagebyCode(i18n.language)
    const [loading, setLoading] = useState(false)
    const [generatedItems, setGeneratedItems] = useState([])
    const [deselectedItems, setdeselectedItems] = useState<string[]>([]);
    const swimlane = useSelector(getSwimlane(swimlaneKey));
    const relevantType = findRelevantMatch(swimlane?.name, insightTypes);
    const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(checkDescription(mapDescription));

    function fetchAiData(description?: string) {
        const prompt = getPrompt({
            language,
            mapName,
            description: description || mapDescription,
            stageName,
            allRelatedInsights,
            allRelatedTouchpoints,
            relatedOpportunities,
            relatedSolutions,
            insightTypes: relevantType ? [relevantType] : insightTypes
        })
        const onSuccess = ({ items }: any) => {
            setGeneratedItems(items)
            setLoading(false)
        }
        setLoading(true)
        alexAssistantCjm({ prompt, max_tokens, keys: `${swimlaneKey}|${stageId}`, onSuccess, onFailed: () => setLoading(false) })
    }
    const handleCheckBox = (event: Event) => {
        const recordIndex = (event.target as HTMLInputElement).id;
        let deselectedItemsUpdated = [...deselectedItems];

        if (deselectedItems.includes(recordIndex)) {
            deselectedItemsUpdated = deselectedItems.filter((index) => index !== recordIndex);
        }
        else {
            deselectedItemsUpdated.push(recordIndex);
        }
        setdeselectedItems(deselectedItemsUpdated);

    }
    const onSubmit = () => {
        onSave(generatedItems.filter((_, index) => !deselectedItems.includes(index.toString())))
        onClose(true)
    }
    const Touchpoints = ({ touchpointName }: any) => {
        const currentTouchpoint = allTouchpoints.find((t: any) => t.name.toLowerCase() === touchpointName.toLowerCase());
        const picture = pictureValidation(currentTouchpoint, sessionId);
        const icon = <img style={{ width: '15px' }} src={picture} loading={"lazy"} alt="" />
        if (!currentTouchpoint) return null;
        return <RelatedItems key={name} {...({ icon, name: touchpointName })} />
    }

    useLayoutEffect(() => {
        if (!isDescriptionEmpty && generatedItems.length === 0)
            fetchAiData()
    }, [])
    if (isDescriptionEmpty) {
        return <FillMapDescription fetchAiData={fetchAiData} setIsDescriptionEmpty={setIsDescriptionEmpty} />
    }

    return (
        <div>
            <Flex align='center' justify='space-between' className='p-2'>
                <Label_ts size='14' color="#C4C4C4">
                    {IconDispatcher("SPARKLES", "me-1")}
                    {t("GENERATED_BY_ALEX")}
                </Label_ts>
                <Button variant='light-link' size='xs' onClick={onClose} className='m-2 p-1'><IconDispatcher type='x' /></Button>
            </Flex>
            <div style={{ minHeight: '300px', maxHeight: '428px', overflow: 'scroll' }}>
                {loading ? <Flex align='center' vertical justify='center' style={{ minHeight: '280px' }}>
                    <Loader />
                    <div style={{ fontSize: '14px' }}>{t("AI_CONTENT_LOADING")}</div>
                </Flex> : <>
                    {generatedItems?.map((item: any, index: number) => {
                        const isSelected = !deselectedItems.includes(index.toString());
                        const insightTypeId = allTypes?.find((t: any) => t.name.trim().toLowerCase() === item?.type?.trim()?.toLowerCase())?.id;
                        return <div key={index} className="d-flex flex-column p-3" >
                            <Flex align='center' className='mb-2' >
                                <CheckBox className="m-0" type="checkbox" id={index} checked={isSelected} onChange={handleCheckBox} />
                                <span className='ms-1'>{item?.title}</span>

                            </Flex>
                            <Label_ts className='flex-wrap three-line-ellipsis mb-1 ' size='12' color="#595959">{StripHtml(item.description)}</Label_ts>
                            <Flex gap={8} className='flex-wrap mt-1' >
                                {item?.relatedTouchpoint && <Touchpoints touchpointName={item?.relatedTouchpoint} />}
                                {item?.relatedInsight && <RelatedItems {...({ icon: IconDispatcher("GEM"), name: item?.relatedInsight })} />}
                                {item?.relatedOpportunity && <RelatedItems  {...({ icon: IconDispatcher("OPPORTUNITY"), name: item?.relatedOpportunity })} />}
                                {item?.relatedSolution && <RelatedItems {...({ icon: IconDispatcher("SOLUTION"), name: item?.relatedSolution })} />}
                                {item?.type && insightTypeId && <InsightType  {...({ typeId: insightTypeId, maxWidth: "100px" } as any)} />}
                            </Flex>
                        </div>
                    })}
                </>}
            </div>
            {!loading && <Flex align='center' justify='flex-end' className='mb-1 p-2'>
                <Button variant='dark-link' size='s' onClick={fetchAiData}>{IconDispatcher('SPARKLES', 'me-1')}{t('REGENERATE')}</Button>
                <Button
                    variant='dark'
                    className='ms-2'
                    size='s'
                    onClick={onSubmit}
                    disabled={generatedItems?.length === deselectedItems?.length}
                >
                    {t('USE_SELECTED')}
                </Button>
            </Flex>}
            <Flex align='center'>
                <TextInfo>{IconDispatcher('INFO', 'me-1')}{t("AI_CONTENT_INFO")}</TextInfo>
            </Flex>
        </div>
    )
}

export default memo(AISuggestion)

function FillMapDescription({ setIsDescriptionEmpty, fetchAiData }: any) {
    const { t } = useTranslation();
    const [cjmDescription, setCjmDescription] = useState("");
    const { cjmIdStr } = getAllMapsParams(useLocation().search)

    const handleSave = () => {
        setIsDescriptionEmpty(false)
        fetchAiData(cjmDescription)
        updateCjmDescription({ id: cjmIdStr, value: cjmDescription });

    }

    return <div className='p-3'>
        <Label>{t("PLEASE_MORE_DETAILS_DESCRIPTION_MAP_AI")}</Label>
        <EditorWrapper>
            <Editor value={cjmDescription} onChange={setCjmDescription} disabled={false} onUpdate={false} />
        </EditorWrapper>
        <Button variant='dark' size='m' onClick={handleSave} disabled={cjmDescription?.length < 20} className='w-100 mt-2'>
            <IconDispatcher type='SPARKLES' styling='me-1' />
            {t("GENERATE")}
        </Button>
    </div>
}
const RelatedItems = ({ icon, name }: any) => {
    return <Flex align='center' gap='4' className='border  px-2 py-0' style={{ width: 'fit-content', fontSize: '14px', borderRadius: '4px' }}>
        {icon}
        <div className='ellipsis'>{name}</div>
    </Flex>
}
function findRelevantMatch(sentence: string, wordsArray: string[]) {
    // Normalize the sentence and split it into words
    const words = sentence.toLowerCase().match(/\b\w+\b/g);
    if (!words) return null;

    // Normalize the words array for comparison but keep the original mapping
    const wordMap: any = new Map();
    wordsArray.forEach(w => wordMap.set(w.toLowerCase(), w)); // Store original case

    // Function to check if a word from the sentence is relevant
    function findMatchingWord(word: string) {
        const lowerWord = word.toLowerCase();

        for (const [normalizedTarget, originalWord] of wordMap.entries()) {
            // Exact match
            if (lowerWord === normalizedTarget) return originalWord;

            // Singular/plural variations (basic check)
            if (lowerWord + 's' === normalizedTarget || lowerWord === normalizedTarget + 's') return originalWord;

            // Basic stemming (removing common suffixes)
            const stemmedWord = lowerWord.replace(/(ing|ed|ly|es|er|ion|able|ment|ness)$/, '');
            const stemmedTarget = normalizedTarget.replace(/(ing|ed|ly|es|er|ion|able|ment|ness)$/, '');
            if (stemmedWord === stemmedTarget) return originalWord;
        }

        return null;
    }

    // Check each word in the sentence
    for (const word of sentence.split(/\s+/)) { // Keep original case of words
        const match = findMatchingWord(word);
        if (match) {
            return match; // Return the matched word from the array in its original form
        }
    }

    return null; // No relevant match found
}

const EditorWrapper = styled.div`
    min-height: 150px;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    padding: 0.5rem;
    width: 100%;
    :focus-within {
        border: 1px solid #333333;
    }
    display: flex;
`;

const Label = styled.div`
	color: #333333;
	font-family: Inter;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
	padding-bottom: 8px;
`;